import {Component, Element, Event, EventEmitter, h, Prop} from '@stencil/core';
import {generatedInputNameAndId} from '@/utils';
import {LabelSizes, SelectSizes} from '@/types';

@Component({
    tag: 'joy-select',
    styleUrl: 'select.scss',
    scoped: true,
})
export class Select {
    private select!: HTMLSelectElement;

    @Element() host!: HTMLJoySelectElement;

    /** Disabled state. **/
    @Prop({reflect: true}) disabled = false;
    /** Invalid state. **/
    @Prop({reflect: true, mutable: true}) invalid = false;
    /** Invalid state message. **/
    @Prop({reflect: true}) invalidMessage = 'Field is required';
    /** Select name attribute to be found by parent form. **/
    @Prop({reflect: true}) name = 'dropdown';
    /** Mandatory or not. **/
    @Prop({reflect: true}) required = false;
    /** Mandatory or not. **/
    @Prop({reflect: true, mutable: true}) value?: string;
    /** Display the required mark or not. Default to false. */
    @Prop({reflect: true}) requiredMark = false;
    /** The label text. */
    @Prop({reflect: true}) label?: string;
    /** The label input's size. */
    @Prop({reflect: true}) labelSize?: LabelSizes;
    /** Inject the right wording if your field is not required. the "-" separator is already handled internally. **/
    @Prop({reflect: true}) optionalLabel?: string;
    /** Default to medium. Use small size for complex and heavy forms. **/
    @Prop({reflect: true}) size?: SelectSizes = 'medium';

    /** Generic custom event name. Name is used by any form field **/
    @Event({eventName: 'value-change'}) valueChange!: EventEmitter<{element: HTMLJoySelectElement; value?: string}>;
    /** Custom event that returns the component instance and its actual value. Bound to select native event **/
    @Event({eventName: 'joy-select-change'}) joySelectChange!: EventEmitter<{element: HTMLJoySelectElement; value?: string}>;

    private onBlur = (ev: Event) => {
        this.invalid = !(ev.target as HTMLSelectElement).checkValidity();
    };

    private onInput = (ev: Event) => {
        this.value = (ev.target as HTMLSelectElement).value;
        const model = {
            element: this.host,
            value: this.value,
        };

        this.valueChange.emit(model);
        this.joySelectChange.emit(model);
    };

    private get labelRelevantSize() {
        if (this.labelSize) {
            return this.labelSize;
        }

        if (this.size !== 'medium') {
            return 'small';
        }

        return 'medium';
    }

    componentDidLoad() {
        const label = this.host.querySelector('[slot="select-label"]') as HTMLLabelElement;

        if (label) {
            label.htmlFor = this.name || generatedInputNameAndId(this.host);
        }

        if (this.value) {
            this.select.value = this.value;
        }
    }

    render() {
        return (
            <div>
                <slot name="select-label">
                    {this.label && (
                        <joy-label
                            required={this.required && this.requiredMark}
                            id={this.selectAriaLabel}
                            optional-label={this.optionalLabel}
                            html-for={this.name || generatedInputNameAndId(this.host)}
                            size={this.labelRelevantSize}
                        >
                            {this.label}
                        </joy-label>
                    )}
                </slot>

                <div
                    class={{
                        'joy-select__wrapper': true,
                        'joy-select--invalid': this.invalid,
                        'joy-select--disabled': this.disabled,
                        [`joy-select--${this.size}`]: true,
                    }}
                >
                    <select
                        ref={(el) => (this.select = el as HTMLSelectElement)}
                        class={{
                            'joy-select': true,
                        }}
                        id={this.name || generatedInputNameAndId(this.host)}
                        name={this.name}
                        aria-invalid={this.invalid}
                        required={this.required}
                        disabled={this.disabled}
                        onBlur={this.onBlur}
                        onChange={this.onInput}
                    >
                        <slot />
                    </select>

                    <joy-icon class="joy-select__chevron" name="chevron-down" />
                </div>
            </div>
        );
    }

    private get selectAriaLabel(): string {
        return 'label-' + generatedInputNameAndId(this.host);
    }
}
